/**
 * BankOffsetMatrix — Lift × MessageContent offset editor for Modbus adapters.
 *
 * Shows a matrix where rows are lifts and columns are MessageContent types
 * found in the adapter's CDO write/read mappings. Each cell is the base
 * register offset for that (lift, MessageContent) combination.
 *
 * The adapter CDO defines base register addresses per message mapping.
 * The bank offset_mapping adds per-lift offsets. Final register address =
 * adapter base + bank lift offset for that MessageContent type.
 *
 * Overlap detection: highlights cells in red when a per-floor expansion
 * would overlap with another MessageContent's register range.
 */
import React, { useEffect, useMemo, useCallback, useState } from "react";
import {
  InputNumber,
  Alert,
  Button,
  Checkbox,
  Popconfirm,
  Table,
  Tag,
  Tooltip,
  Switch,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { expandOffsets, classifyBankCell } from "../../components/adapters/offsetMatrixHelpers";
import { valuedMessageTToCompositeKey } from "../../../flatbuffers/types/valuedMessageBuilder";
import {
  messageContentToCategoryKey,
  getDisplayName,
  parseCompositeKey,
} from "../../../flatbuffers/types/ln2MessageOptions";
import {
  detectBankMatrixOverlaps,
  formatOverlapTooltip,
  categoryKeyToMcNumber,
  columnDirectionsAreOpposed,
  type MatrixColumnSpec,
  type OverlapInfo,
  type IntraContentLookup,
  intraLookup,
} from "../../components/adapters/offsetOverlapDetection";
import { computeColumnByteSize } from "../../components/adapters/adapterSizeMath";
import {
  deriveColumnDirection,
  type MatrixKeyMappingSubset,
} from "../../components/adapters/AdapterOffsetMatrix";
import { ModbusReadType } from "../../../flatbuffers/generated/config/modbus-read-type";
import { ModbusWriteType } from "../../../flatbuffers/generated/config/modbus-write-type";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { devLog } from "../../../utils/devLog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type { MappedMessageType } from "../../components/adapters/adapterColumnTypes";

export type { MappedMessageType } from "../../components/adapters/adapterColumnTypes";

/** A single row in the offset matrix (one per lift). */
interface OffsetRow {
  key: string;
  liftIndex: number;
  liftName: string;
  liftId: string;
  /** Offsets keyed by categoryKey */
  offsets: Record<string, number>;
  /** Overlap warnings keyed by categoryKey */
  overlaps: Record<string, string>;
}

/** Individual key/mapping with byte+bit position for fine-grained overlap detection. */
export interface AdapterKeyInfo {
  /** Category key (e.g., "status", "fault") */
  categoryKey: string;
  /** Byte offset within the message type's register range */
  byteOffset: number;
  /** Bit position within the byte (0-7), or -1 for byte-mode */
  bit: number;
  /** Human-readable label (e.g., "Fault::Safety") */
  label: string;
}

interface BankOffsetMatrixProps {
  /** Lift entries for this bank */
  lifts: { id: string; name: string }[];
  /** MessageContent types found in the adapter's CDO */
  mappedTypes: MappedMessageType[];
  /** Current offset values: [liftIndex] = {categoryKey: offset} */
  offsets: Record<string, number>[];
  /** Called when an offset value changes */
  onOffsetsChange: (offsets: Record<string, number>[]) => void;
  /** Number of floors in the bank (for overlap detection) */
  floorCount: number;
  /** Whether intra-content register sharing is enabled (global adapter toggle).
   *  When true, different MessageContent types may share the same byte
   *  as long as their bit positions don't collide. */
  intraContent: boolean;
  /** Individual adapter keys with byte+bit positions for fine-grained overlap detection. */
  adapterKeys: AdapterKeyInfo[];
  /** The adapter's LN1 key mappings, used ONLY to derive each column's
   *  direction.
   *
   *  Needed because for an LN1-family adapter (LN1/RVU/CGA/LOBBYVISION)
   *  `adapterKeys` is deliberately empty — per-key detection produces false
   *  point collisions there, so detection falls back to comparing whole column
   *  ranges. That fallback is already direction-aware
   *  (`columnDirectionsAreOpposed`), but it can only act on a direction the
   *  caller supplies, and this component never supplied one. The result was the
   *  bank matrix flagging a Control/Traffic pair on one byte that the adapter
   *  matrix correctly excused — the two screens disagreeing about a pair that
   *  is legal by design. */
  ln1KeyMappings?: MatrixKeyMappingSubset;
  /** The adapter's per-MessageContent intra-content flags.
   *
   *  Supplied because `intraContent` above is a single any-true rollup, and
   *  feeding that to the detector makes ONE flagged category suppress EVERY
   *  cross-category overlap on this page — while the adapter page requires
   *  BOTH participants to be flagged. So an adapter with intra-content on for
   *  Fault alone hid a genuine Status/Control collision here and showed it
   *  there. When present these flags are consulted per category, exactly as
   *  the adapter page does; when absent the old boolean still applies, so
   *  callers that do not supply them are unaffected. */
  intraContentFlags?: Partial<Record<number | string, boolean[]>>;
  /** Called when overlap state changes — parent can use to gate save buttons. */
  onOverlapChange: (hasOverlaps: boolean) => void;
  /** Column categoryKeys that are disabled for this bank. Disabled columns
   *  are greyed out, excluded from overlap detection, and not saved to
   *  offset_mapping. Useful when a bank doesn't use certain adapter
   *  mappings (e.g. no rear doors). */
  disabledColumns?: Set<string>;
  /** Called when a column is enabled/disabled. */
  onDisabledColumnsChange?: (disabled: Set<string>) => void;
  /** Per-lift adapter base offsets. When provided, enables the Simplified Mode
   *  toggle. In simplified mode, the user specifies one "base offset" per lift
   *  that is added to these adapter defaults for every message category. */
  adapterDefaults?: Record<string, number>[];
  /** Optional hydrated simplified base offsets for reopening saved banks in
   *  simplified mode when their persisted matrix is representable that way. */
  initialSimpleBaseOffsets?: number[] | null;
  /** Register-type-grouped mapped types (only for Modbus adapters).
   *  When provided, enables the "By Register Type" toggle. */
  registerTypeMappedTypes?: MappedMessageType[];
  /** Maps each LN2 categoryKey to its register type key.
   *  Used to convert offsets between category and register-type views. */
  categoryToRegType?: Record<string, string>;

  /**
   * New in ship-adapter-offset-delta-matrix: per-category initial+delta
   * pulled from the bank's adapter. When supplied, bank cells default to
   * expandOffsets(initial, delta, numCars) and a cell is classified as
   * "derived" when stored === derived, "overridden" otherwise. When
   * undefined, fallback to the legacy behavior (every cell = stored value,
   * always treated as overridden for styling purposes).
   */
  adapterDefaults2Row?: Record<string, { initial: number; delta: number }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the number of registers a mapping occupies.
 *  ByteMessage floor expansion only changes the value (not the offset),
 *  so it always uses 1 register. BitMessage floor expansion packs floors
 *  into bits — ceil(floorCount / bitsPerRegister) registers. */
function computeRegCount(mt: MappedMessageType, floorCount: number): number {
  if (mt.isByteExpansion) return 1;
  if (!mt.floorsBased) return 1;
  const bpr = mt.bitsPerRegister || 16;
  return Math.ceil(floorCount / bpr);
}

/**
 * Byte spans of every category a column stands for.
 *
 * A MERGED column represents more than one category — the matrix joins them
 * under a label such as "Status + Usage" — and sizing it from one member left it
 * too narrow to cover what it renders. Merging groups by (initial offset, car
 * delta), so every member starts at the same address and the widest member is
 * the column's width.
 *
 * Only a merged column may consult `categoryKeys`. Everywhere else `categoryKey`
 * is the MORE specific identity: an individual column keys on
 * "traffic:normal:0" while its `categoryKeys` holds the bare "traffic", so
 * measuring by the latter loses the message and falls back to the span of the
 * whole category — the inflation `computeColumnByteSize` exists to avoid. That
 * is how Traffic::P, one position register, came to claim the two bytes its
 * category occupies and collide with the byte above it.
 */
function mappedTypeSpans(
  mt: MappedMessageType,
  mappings: MatrixKeyMappingSubset,
  floors: number,
): number[] {
  const keys = (mt.categoryKeys?.length ?? 0) > 1 ? mt.categoryKeys! : [mt.categoryKey];
  return keys.map((ck) => computeColumnByteSize(mappings, ck, categoryKeyToMcNumber(ck), floors));
}

/** Category key → human-readable label */
const CATEGORY_LABELS: Record<string, string> = {
  status: "Status",
  traffic: "Traffic",
  usage: "Usage",
  service: "Service",
  control: "Control",
  fault: "Fault",
  group_supervisory_status: "Group Supervisory",
  group_control: "Group Control",
  controller_info: "Controller Info",
};

/** Canonical register type keys used as categoryKey in register-type mode */
export const REGISTER_TYPE_KEY = {
  HOLDING: "holding_register",
  COIL: "coil",
  INPUT: "input_register",
  DISCRETE: "discrete_input",
  WRITE_REG: "write_register",
} as const;

/** Register type key -> human-readable label */
const REGISTER_TYPE_LABELS: Record<string, string> = {
  [REGISTER_TYPE_KEY.HOLDING]: "Holding Register",
  [REGISTER_TYPE_KEY.COIL]: "Coil",
  [REGISTER_TYPE_KEY.INPUT]: "Input Register",
  [REGISTER_TYPE_KEY.DISCRETE]: "Discrete Input",
  [REGISTER_TYPE_KEY.WRITE_REG]: "Write Register",
};

/** MessageContent enum discriminant → category key */
const MESSAGE_CONTENT_TO_KEY: Record<number, string> = {
  1: "status",
  2: "traffic",
  3: "usage",
  4: "service",
  5: "control",
  6: "fault",
  7: "group_supervisory_status",
  8: "group_control",
  9: "controller_info",
};

/** Protocols that use LN1-style offset_mapping + keys */
const LN1_PROTOCOLS = new Set(["LN1", "RVU", "CGA", "LOBBYVISION"]);

/**
 * Resolve an adapter protocol from either a FlatBuffer numeric enum (AdapterT
 * path, e.g. `protocolType: 3`) or a JSON string (`protocol_type: "LN1"`).
 * Returns the uppercase string label so callers can compare against a set.
 */
function resolveAdapterProtocol(adapter: any): string {
  const raw = adapter?.protocolType ?? adapter?.protocol_type;
  if (typeof raw === "number") {
    return (AdapterProtocol[raw as AdapterProtocol] ?? "").toUpperCase();
  }
  return (raw ?? "").toString().toUpperCase();
}

/**
 * Extract mapped MessageContent types from an adapter.
 *
 * For **Modbus** adapters: reads `cdo_input.msg_to_ln2_map` / `ln2_to_msg_map`
 * to find message categories and base register addresses.
 *
 * For **LN1/RVU/CGA/LobbyVision** adapters: reads `adapter.keys` to find
 * which MessageContent types are mapped, and `adapter.offset_mapping` for
 * base byte offsets (from the first unit/lift).
 */
/** Per-MessageContent intra-content flag map. `true` means this category
 *  shares a register with other flagged categories in the same adapter and
 *  should be merged into a single matrix column. */
export type IntraContentFlagMap = Record<number, boolean>;

export function extractMappedMessageTypes(
  cdoInput: any,
  adapter?: any,
  intraContent?: boolean | IntraContentFlagMap,
): MappedMessageType[] {
  const protocolType = resolveAdapterProtocol(adapter);

  // --- LN1/RVU/CGA/LobbyVision path: read keys + offset_mapping ---
  // Check this BEFORE the Modbus fallback so that an LN1 adapter with a
  // non-null (even empty) cdo_template doesn't get misrouted to Modbus.
  if (LN1_PROTOCOLS.has(protocolType) && adapter) {
    return extractLn1MappedTypes(adapter, intraContent ?? false);
  }

  // --- Modbus path: parse CDO read/write map entries ---
  if (protocolType === "MODBUS" || (protocolType === "" && cdoInput)) {
    return extractModbusMappedTypes(cdoInput);
  }

  // Fallback: try both paths
  const modbus = extractModbusMappedTypes(cdoInput);
  if (modbus.length > 0) return modbus;
  if (adapter) return extractLn1MappedTypes(adapter, intraContent ?? false);
  return [];
}

/** Extract from Modbus CDO — supports both old JSON format (msg_to_ln2_map)
 *  and new binary template format (reads/writes with label as msgId). */
function extractModbusMappedTypes(cdoInput: any): MappedMessageType[] {
  // New binary template format: reads[] + writes[] with label field
  const reads: any[] = cdoInput?.reads || [];
  const writes: any[] = cdoInput?.writes || [];

  // Old JSON format: msg_to_ln2_map[] + ln2_to_msg_map[]
  const oldReads: any[] = cdoInput?.msg_to_ln2_map || [];
  const oldWrites: any[] = cdoInput?.ln2_to_msg_map || [];

  const allEntries = [...reads, ...writes, ...oldReads, ...oldWrites];

  devLog(
    "[OffsetMatrix] extractModbusMappedTypes:",
    "reads:",
    reads.length,
    "writes:",
    writes.length,
    "oldReads:",
    oldReads.length,
    "oldWrites:",
    oldWrites.length,
    "total:",
    allEntries.length,
    "cdoInput keys:",
    cdoInput ? Object.keys(cdoInput) : "null",
    "first entry:",
    allEntries[0],
  );

  const seen = new Set<string>();
  const result: MappedMessageType[] = [];

  allEntries.forEach((entry: any, idx: number) => {
    // Derive composite key: try FlatBuffer unpack (camelCase), API JSON
    // (snake_case), then legacy label/msgId fallback.
    let msgId: string = "";
    const vm = entry?.ln2Message || entry?.ln2_message;

    devLog(
      `[OffsetMatrix] entry[${idx}]:`,
      "label:",
      entry?.label,
      "ln2Message:",
      entry?.ln2Message ? "present" : "absent",
      "ln2_message:",
      entry?.ln2_message ? "present" : "absent",
      "vm?.msg:",
      vm?.msg ? "present" : "absent",
      "vm structure:",
      vm ? Object.keys(vm) : "null",
    );

    if (vm?.msg) {
      // Try typed reverse function (works on FlatBuffer-unpacked camelCase objects)
      msgId = valuedMessageTToCompositeKey(vm) || "";
      devLog(`[OffsetMatrix] entry[${idx}] valuedMessageTToCompositeKey:`, msgId);
      // Fallback: extract MessageContent discriminant from API JSON.
      // Go JSON: {msg:{content:{Type:2,Value:{message:{Type:4,...}}}}}
      // BitMaskMessageT has json:"message", MessageContentT has no json tag (Type is capitalized).
      if (!msgId) {
        const encoded = vm.msg.content || vm.msg.Content;
        const inner = encoded?.Value || encoded?.value;
        // inner is BitMaskMessageT or OneHotMessageT — field "message" (json tag) contains MessageContentT
        const msgContent = inner?.message || inner?.Message;
        const mcType = msgContent?.Type ?? msgContent?.type ?? 0;
        devLog(
          `[OffsetMatrix] entry[${idx}] API JSON fallback: encoded:`,
          encoded,
          "inner:",
          inner,
          "msgContent:",
          msgContent,
          "mcType:",
          mcType,
        );
        if (typeof mcType === "number" && mcType > 0) {
          const catKey = messageContentToCategoryKey(mcType as any);
          if (catKey) msgId = `${catKey}:normal:0`;
        }
      }
    }
    if (!msgId) {
      msgId = entry?.label || entry?.msgId || entry?.ln2_message?.msg_id || "";
      devLog(`[OffsetMatrix] entry[${idx}] legacy fallback msgId:`, msgId);
    }
    const parts = msgId.split(":");
    if (parts.length < 2) return;

    const categoryKey = parts[0];
    if (seen.has(categoryKey)) return;
    seen.add(categoryKey);

    const baseAddr =
      entry.registerAddress ??
      entry.register_address ??
      (entry.request?.message?.address as number) ??
      0;

    const isBitmask =
      entry.interpretation === 1 || // ModbusInterpretation.BitmaskSingleBit
      entry.interpretation?.type === "BitmaskData8" ||
      entry.interpretation?.type === "BitmaskData16";

    // For bitmask: multiple floors pack into one register (8 or 16 bits).
    // For WholeValue (By Value): each floor uses one full register, so
    // bitsPerRegister = 1 (one floor per register).
    let bpr = 16;
    if (isBitmask) {
      bpr = entry.bitsPerRegister ?? entry.interpretation?.bits_per_register ?? 16;
    } else if (entry.floorsBased ?? entry.floors_based ?? false) {
      bpr = 1; // WholeValue + floorsBased: one register per floor
    }

    // Same fallback as the LN1 path: when this Modbus entry is units-based
    // and the adapter ends up persisting delta=0 for the column, the bank
    // matrix would derive every car onto the same register and the
    // cross-lift bleed validator would fire. One register per car is the
    // canonical Modbus per-car stride for a units-based scalar mapping —
    // expose it so `effectiveOffsets` can substitute it when the persisted
    // delta is 0. Floors-based-only entries stay undefined; they vary by
    // floor not by car and a non-zero delta would be wrong.
    const isUnits = entry.unitsBased ?? entry.units_based ?? false;
    const groupedUnitsBasedDelta = isUnits ? 1 : undefined;

    result.push({
      label: CATEGORY_LABELS[categoryKey] || categoryKey,
      categoryKey,
      categoryKeys: [categoryKey],
      baseAddress: baseAddr,
      floorsBased: entry.floorsBased ?? entry.floors_based ?? false,
      bitsPerRegister: bpr,
      ...(groupedUnitsBasedDelta !== undefined ? { groupedUnitsBasedDelta } : {}),
    });
  });

  return result;
}

/**
 * Extract Modbus register-type-grouped MappedMessageType entries from a CDO.
 *
 * Rather than grouping columns by LN2 MessageContent category, this groups
 * them by the Modbus register type (Holding Register, Coil, Input Register,
 * Discrete Input, Write Register). Only register types present in the CDO
 * are returned.
 *
 * Also returns a `categoryToRegType` map so the parent can convert between
 * the per-category and per-register-type offset representations.
 */
export function extractModbusRegisterTypeMappedTypes(cdoInput: any): {
  types: MappedMessageType[];
  categoryToRegType: Record<string, string>;
} {
  const reads: any[] = cdoInput?.reads || [];
  const writes: any[] = cdoInput?.writes || [];

  // Old JSON format: msg_to_ln2_map[] + ln2_to_msg_map[]
  const oldReads: any[] = cdoInput?.msg_to_ln2_map || [];
  const oldWrites: any[] = cdoInput?.ln2_to_msg_map || [];

  if (
    reads.length === 0 &&
    writes.length === 0 &&
    oldReads.length === 0 &&
    oldWrites.length === 0
  ) {
    return { types: [], categoryToRegType: {} };
  }

  /**
   * Map a Modbus Request/Response union discriminant (Type field) to a
   * register type key. The Request and Response unions share the same
   * ordering for the first 9 variants in modbus.fbs.
   */
  const UNION_DISC_TO_REG_TYPE: Record<number, string> = {
    1: REGISTER_TYPE_KEY.COIL, // ReadCoils*
    2: REGISTER_TYPE_KEY.DISCRETE, // ReadDiscreteInputs*
    3: REGISTER_TYPE_KEY.HOLDING, // ReadHoldingRegisters*
    4: REGISTER_TYPE_KEY.INPUT, // ReadInputRegisters*
    5: REGISTER_TYPE_KEY.HOLDING, // ReadWriteMultiple*
    6: REGISTER_TYPE_KEY.COIL, // WriteSingleCoil*
    7: REGISTER_TYPE_KEY.COIL, // WriteMultipleCoils*
    8: REGISTER_TYPE_KEY.WRITE_REG, // WriteSingleRegister*
    9: REGISTER_TYPE_KEY.WRITE_REG, // WriteMultipleRegisters*
  };

  /** Map a Request.kind string to a register type key. */
  const KIND_TO_REG_TYPE: Record<string, string> = {
    ReadCoilsRequest: REGISTER_TYPE_KEY.COIL,
    ReadDiscreteInputsRequest: REGISTER_TYPE_KEY.DISCRETE,
    ReadHoldingRegistersRequest: REGISTER_TYPE_KEY.HOLDING,
    ReadInputRegistersRequest: REGISTER_TYPE_KEY.INPUT,
    ReadWriteMultipleRegistersRequest: REGISTER_TYPE_KEY.HOLDING,
    WriteSingleCoilRequest: REGISTER_TYPE_KEY.COIL,
    WriteMultipleCoilRequests: REGISTER_TYPE_KEY.COIL,
    WriteSingleRegisterRequest: REGISTER_TYPE_KEY.WRITE_REG,
    WriteMultipleRegistersRequest: REGISTER_TYPE_KEY.WRITE_REG,
    ReadCoilsResponse: REGISTER_TYPE_KEY.COIL,
    ReadDiscreteInputsResponse: REGISTER_TYPE_KEY.DISCRETE,
    ReadHoldingRegistersResponse: REGISTER_TYPE_KEY.HOLDING,
    ReadInputRegistersResponse: REGISTER_TYPE_KEY.INPUT,
    ReadWriteMultipleRegistersResponse: REGISTER_TYPE_KEY.HOLDING,
    WriteSingleCoilResponse: REGISTER_TYPE_KEY.COIL,
    WriteMultipleCoilsResponse: REGISTER_TYPE_KEY.COIL,
    WriteSingleRegisterResponse: REGISTER_TYPE_KEY.WRITE_REG,
    WriteMultipleRegistersResponse: REGISTER_TYPE_KEY.WRITE_REG,
  };

  /** Per-register-type accumulator */
  type RegTypeBucket = {
    categoryKeys: Set<string>;
    minBaseAddr: number;
    anyFloorsBased: boolean;
    anyUnitsBased: boolean;
    maxBitsPerRegister: number;
  };

  const buckets = new Map<string, RegTypeBucket>();
  const categoryToRegType: Record<string, string> = {};

  /**
   * Resolve the LN2 categoryKey for a CDO entry using the same logic as
   * extractModbusMappedTypes. Falls back to label/msgId string splitting.
   */
  function resolveCategoryKey(entry: any): string | null {
    const vm = entry?.ln2Message || entry?.ln2_message;
    let msgId = "";
    if (vm?.msg) {
      msgId = valuedMessageTToCompositeKey(vm) || "";
      if (!msgId) {
        const encoded = vm.msg.content || vm.msg.Content;
        const inner = encoded?.Value || encoded?.value;
        const msgContent = inner?.message || inner?.Message;
        const mcType = msgContent?.Type ?? msgContent?.type ?? 0;
        if (typeof mcType === "number" && mcType > 0) {
          const catKey = messageContentToCategoryKey(mcType as any);
          if (catKey) msgId = `${catKey}:normal:0`;
        }
      }
    }
    if (!msgId) {
      msgId = entry?.label || entry?.msgId || entry?.ln2_message?.msg_id || "";
    }
    const parts = msgId.split(":");
    if (parts.length < 2) return null;
    return parts[0];
  }

  function ensureBucket(regTypeKey: string): RegTypeBucket {
    if (!buckets.has(regTypeKey)) {
      buckets.set(regTypeKey, {
        categoryKeys: new Set(),
        minBaseAddr: Infinity,
        anyFloorsBased: false,
        anyUnitsBased: false,
        maxBitsPerRegister: 16,
      });
    }
    return buckets.get(regTypeKey)!;
  }

  reads.forEach((entry: any) => {
    const readType: number =
      entry.readType ?? entry.read_type ?? ModbusReadType.ReadHoldingRegisters;
    let regTypeKey: string;
    if (readType === ModbusReadType.ReadCoils) {
      regTypeKey = REGISTER_TYPE_KEY.COIL;
    } else if (readType === ModbusReadType.ReadDiscreteInputs) {
      regTypeKey = REGISTER_TYPE_KEY.DISCRETE;
    } else if (readType === ModbusReadType.ReadInputRegisters) {
      regTypeKey = REGISTER_TYPE_KEY.INPUT;
    } else {
      regTypeKey = REGISTER_TYPE_KEY.HOLDING;
    }

    const catKey = resolveCategoryKey(entry);
    const baseAddr =
      entry.registerAddress ??
      entry.register_address ??
      (entry.request?.message?.address as number) ??
      0;
    const floorsBased = entry.floorsBased ?? entry.floors_based ?? false;
    const isBitmask =
      entry.interpretation === 1 ||
      entry.interpretation?.type === "BitmaskData8" ||
      entry.interpretation?.type === "BitmaskData16";
    let bpr = 16;
    if (isBitmask) {
      bpr = entry.bitsPerRegister ?? entry.interpretation?.bits_per_register ?? 16;
    } else if (floorsBased) {
      bpr = 1;
    }

    const bucket = ensureBucket(regTypeKey);
    if (catKey) {
      bucket.categoryKeys.add(catKey);
      categoryToRegType[catKey] = regTypeKey;
    }
    if (baseAddr < bucket.minBaseAddr) bucket.minBaseAddr = baseAddr;
    if (floorsBased) bucket.anyFloorsBased = true;
    if (entry.unitsBased ?? entry.units_based ?? false) bucket.anyUnitsBased = true;
    if (bpr > bucket.maxBitsPerRegister) bucket.maxBitsPerRegister = bpr;
  });

  writes.forEach((entry: any) => {
    const writeType: number =
      entry.writeType ?? entry.write_type ?? ModbusWriteType.WriteSingleRegister;
    let regTypeKey: string;
    if (
      writeType === ModbusWriteType.WriteSingleCoil ||
      writeType === ModbusWriteType.WriteMultipleCoils
    ) {
      regTypeKey = REGISTER_TYPE_KEY.COIL;
    } else {
      // WriteSingleRegister (0), WriteMultipleRegisters (2), MaskWriteRegister (4)
      regTypeKey = REGISTER_TYPE_KEY.WRITE_REG;
    }

    const catKey = resolveCategoryKey(entry);
    const baseAddr =
      entry.registerAddress ??
      entry.register_address ??
      (entry.request?.message?.address as number) ??
      0;
    const floorsBased = entry.floorsBased ?? entry.floors_based ?? false;
    const isBitmask =
      entry.interpretation === 1 ||
      entry.interpretation?.type === "BitmaskData8" ||
      entry.interpretation?.type === "BitmaskData16";
    let bpr = 16;
    if (isBitmask) {
      bpr = entry.bitsPerRegister ?? entry.interpretation?.bits_per_register ?? 16;
    } else if (floorsBased) {
      bpr = 1;
    }

    const bucket = ensureBucket(regTypeKey);
    if (catKey) {
      bucket.categoryKeys.add(catKey);
      categoryToRegType[catKey] = regTypeKey;
    }
    if (baseAddr < bucket.minBaseAddr) bucket.minBaseAddr = baseAddr;
    if (floorsBased) bucket.anyFloorsBased = true;
    if (entry.unitsBased ?? entry.units_based ?? false) bucket.anyUnitsBased = true;
    if (bpr > bucket.maxBitsPerRegister) bucket.maxBitsPerRegister = bpr;
  });

  // --- Old format: msg_to_ln2_map (reads) + ln2_to_msg_map (writes) ---
  // Derive register type from the Request/Response union discriminant.

  /** Extract the register type key from a request/response message union. */
  function regTypeFromUnion(msg: any): string | null {
    if (!msg) return null;
    // String kind (TS Object API): msg.kind = "ReadHoldingRegistersRequest"
    if (typeof msg.kind === "string" && KIND_TO_REG_TYPE[msg.kind]) {
      return KIND_TO_REG_TYPE[msg.kind];
    }
    // Numeric union discriminant (Go JSON / FlatBuffer): msg.Type = 3
    const disc = msg.Type ?? msg.type;
    if (typeof disc === "number" && UNION_DISC_TO_REG_TYPE[disc]) {
      return UNION_DISC_TO_REG_TYPE[disc];
    }
    return null;
  }

  function processOldEntry(entry: any, isWrite: boolean): void {
    // Determine register type from union discriminant
    // msg_to_ln2_map entries: key_1.message (ResponseMessage) or request.message
    // ln2_to_msg_map entries: val.message (RequestMessage) or request.message
    const unionMsg = isWrite
      ? entry?.val?.message || entry?.request?.message
      : entry?.key_1?.message || entry?.request?.message;
    const regTypeKey = regTypeFromUnion(unionMsg) ?? REGISTER_TYPE_KEY.HOLDING;

    // Resolve LN2 category from the valued message
    // msg_to_ln2_map: entry.val is the FEMessage/ValuedMessage
    // ln2_to_msg_map: entry.key is the FEMessage/ValuedMessage
    const catKey = resolveCategoryKey(
      isWrite
        ? { ln2Message: entry?.key, ln2_message: entry?.key }
        : { ln2Message: entry?.val, ln2_message: entry?.val },
    );

    const baseAddr = entry?.key_2 ?? entry?.register_address ?? 0;
    const floorsBased = entry?.floors_based ?? false;
    const unitsBased = entry?.units_based ?? false;

    const bucket = ensureBucket(regTypeKey);
    if (catKey) {
      bucket.categoryKeys.add(catKey);
      categoryToRegType[catKey] = regTypeKey;
    }
    if (baseAddr < bucket.minBaseAddr) bucket.minBaseAddr = baseAddr;
    if (floorsBased) bucket.anyFloorsBased = true;
    if (unitsBased) bucket.anyUnitsBased = true;
  }

  oldReads.forEach((entry: any) => processOldEntry(entry, false));
  oldWrites.forEach((entry: any) => processOldEntry(entry, true));

  // Emit results in canonical order: HOLDING, COIL, INPUT, DISCRETE, WRITE_REG
  const orderedKeys = [
    REGISTER_TYPE_KEY.HOLDING,
    REGISTER_TYPE_KEY.COIL,
    REGISTER_TYPE_KEY.INPUT,
    REGISTER_TYPE_KEY.DISCRETE,
    REGISTER_TYPE_KEY.WRITE_REG,
  ];

  // Dedup category ownership: the offset_mapping storage is per LN2 category,
  // so a single catKey can only back ONE register-type column. When the same
  // category is served by multiple Modbus function codes (e.g. status read via
  // both ReadHoldingRegisters and ReadInputRegisters), claim it for the
  // canonical-first register type and remove it from the others. Otherwise
  // multiple columns would share backing storage and editing one would leak
  // into the other after any register ↔ category round-trip.
  const canonicalIndex: Record<string, number> = {};
  orderedKeys.forEach((k, i) => {
    canonicalIndex[k] = i;
  });
  const rankOf = (k: string): number => canonicalIndex[k] ?? Number.POSITIVE_INFINITY;

  const catClaimants = new Map<string, string[]>();
  buckets.forEach((bucket, regTypeKey) => {
    bucket.categoryKeys.forEach((catKey) => {
      const list = catClaimants.get(catKey) ?? [];
      list.push(regTypeKey);
      catClaimants.set(catKey, list);
    });
  });
  catClaimants.forEach((regTypes, catKey) => {
    if (regTypes.length <= 1) return;
    const winner = regTypes.reduce((a, b) => (rankOf(a) <= rankOf(b) ? a : b));
    regTypes.forEach((rtk) => {
      if (rtk !== winner) buckets.get(rtk)?.categoryKeys.delete(catKey);
    });
    categoryToRegType[catKey] = winner;
  });

  const types: MappedMessageType[] = [];
  orderedKeys.forEach((regTypeKey) => {
    const bucket = buckets.get(regTypeKey);
    if (!bucket) return;
    // A bucket with no claimed categories would render a column whose cells
    // cannot persist (nothing to write to offset_mapping). Omit it.
    if (bucket.categoryKeys.size === 0) return;
    types.push({
      label: REGISTER_TYPE_LABELS[regTypeKey],
      categoryKey: regTypeKey,
      categoryKeys: Array.from(bucket.categoryKeys),
      baseAddress: bucket.minBaseAddr === Infinity ? 0 : bucket.minBaseAddr,
      floorsBased: bucket.anyFloorsBased,
      bitsPerRegister: bucket.maxBitsPerRegister,
      // Same per-car-stride fallback as the LN1 / Modbus per-category paths:
      // when any entry feeding this register-type column is units-based and
      // the persisted delta on the adapter is 0, the bank matrix should
      // derive each car onto its own register rather than collapsing them.
      ...(bucket.anyUnitsBased ? { groupedUnitsBasedDelta: 1 } : {}),
    });
  });

  return { types, categoryToRegType };
}

/** Build a human-readable label for an individual bank_level key.
 *  Uses getDisplayName to produce e.g. "Control: Energize Car Call". */
function individualKeyLabel(k: any): string {
  const compositeKey = individualColumnKey(k);
  const name = getDisplayName(compositeKey);
  // getDisplayName returns "--" for unknown keys; fall back to category + value
  if (name === "--") {
    const mc: number = k.messageContent ?? k.message_content ?? 0;
    const catKey = MESSAGE_CONTENT_TO_KEY[mc] || String(mc);
    const catLabel = CATEGORY_LABELS[catKey] || catKey;
    return `${catLabel} #${k.messageValue ?? k.message_value ?? 0}`;
  }
  return name;
}

/** Build the composite key used as the column identifier for an individual
 *  bank_level mapping. Format: "categoryKey:normal:enumValue".
 *  Exported so StepBanks can reuse the same key construction for overlap detection. */
export function individualColumnKey(k: any): string {
  const mc: number = k.messageContent ?? k.message_content ?? 0;
  const catKey = MESSAGE_CONTENT_TO_KEY[mc] || String(mc);
  return `${catKey}:normal:${k.messageValue ?? k.message_value ?? 0}`;
}

/** Extract from LN1-style adapter keys + offset_mapping + cdo_input.
 *  When `intraContent` is `true` every category is treated as intra-content
 *  flagged (legacy behavior). When a `Record<MessageContent, boolean>` is
 *  passed, only categories whose flag is truthy participate in merging;
 *  unflagged categories render as their own columns regardless. All
 *  intra-flagged categories merge into a single column because they are
 *  declared to share a register — the single matrix cell drives every
 *  member when written.
 *  Keys with bank_level=true get their own individual columns instead of
 *  being grouped under their MessageContent category. */
export function extractLn1MappedTypes(
  adapter: any,
  intraContent: boolean | IntraContentFlagMap = false,
): MappedMessageType[] {
  const keys: any[] = adapter?.keys || [];
  const rawOm = adapter?.offset_mapping;
  // offset_mapping may be an array of OffsetMappingEntry objects OR an object
  // keyed by message_type. Normalise to a flat array.
  let offsetEntries: any[] = [];
  if (Array.isArray(rawOm)) {
    offsetEntries = rawOm;
  } else if (rawOm && typeof rawOm === "object") {
    offsetEntries = Object.values(rawOm);
  }

  // Partition keys into own-column (bank_level OR individual) vs grouped
  // (both flags false). bank_level implies "no per-car delta", individual
  // implies "own column with per-car delta" — bank_level wins when both set.
  const ownColumnKeys: any[] = [];
  const normalKeys: any[] = [];
  keys.forEach((k: any) => {
    const bl = k.bankLevel ?? k.bank_level ?? false;
    const ind = k.individual ?? false;
    if (bl || ind) {
      ownColumnKeys.push(k);
    } else {
      normalKeys.push(k);
    }
  });

  // Collect unique MessageContent types from NORMAL key mappings only.
  // Bank_level keys get their own columns below.
  const seenContent = new Set<number>();
  normalKeys.forEach((k: any) => {
    const mc = k.messageContent ?? k.message_content;
    if (mc && mc > 0) {
      seenContent.add(mc);
    }
  });

  // For LN1 adapters, the offset_mapping message_type values use the
  // old message_types table IDs which do NOT match the MessageContent
  // enum values used for category lookup. Since deriveOffsetsFromAdapter
  // handles the message_type → categoryKey translation separately,
  // set all base addresses to 0 here. The matrix cells will contain
  // absolute byte offsets directly.
  const baseByType = new Map<number, number>();
  offsetEntries.forEach((entry: any) => {
    if (entry.unit_number === 0 && entry.message_type != null) {
      baseByType.set(entry.message_type, 0);
    }
  });

  // Compute (initialOffset, carDelta) per MC from offset_mapping. Used by the
  // intra-content grouping pass so that flagged categories only merge when they
  // share the same initial offset AND the same car delta. Two shapes are
  // supported because callers feed different formats:
  //   1. Object-keyed by MC enum (cdoState.ln1UnitOffsets):
  //      `{[mc]: [u0, u1, u2, ...]}` — initial=u0, delta=u1-u0.
  //   2. Array of OffsetMappingEntry (canonical adapter response):
  //      `[{message_type, unit_number, offset}, ...]` — collect per-mt unit
  //      offsets, take unit_number=0 for initial, unit_number=1 for delta.
  // MCs absent from the source default to (0, 0) so the bucketing is total.
  const initialDeltaByMc = new Map<number, [number, number]>();
  if (Array.isArray(rawOm)) {
    const byMt = new Map<number, Map<number, number>>();
    rawOm.forEach((entry: any) => {
      if (!entry || typeof entry !== "object") return;
      const mt = entry.message_type;
      if (typeof mt !== "number" || !Number.isFinite(mt)) return;
      const un = typeof entry.unit_number === "number" ? entry.unit_number : 0;
      const off = typeof entry.offset === "number" ? entry.offset : 0;
      if (!byMt.has(mt)) byMt.set(mt, new Map());
      byMt.get(mt)!.set(un, off);
    });
    byMt.forEach((unitMap, mt) => {
      const u0 = unitMap.get(0) ?? 0;
      const u1 = unitMap.has(1) ? unitMap.get(1)! : u0;
      initialDeltaByMc.set(mt, [u0, u1 - u0]);
    });
  } else if (rawOm && typeof rawOm === "object") {
    Object.entries(rawOm as Record<string, unknown>).forEach(([key, val]) => {
      const mc = Number(key);
      if (!Number.isFinite(mc) || mc <= 0) return;
      if (!Array.isArray(val)) return;
      const u0 = typeof val[0] === "number" ? val[0] : 0;
      const u1 = val.length >= 2 && typeof val[1] === "number" ? val[1] : u0;
      initialDeltaByMc.set(mc, [u0, u1 - u0]);
    });
  }

  // Build grouped category entries (non-bank_level keys)
  const entries: { mc: number; categoryKey: string; base: number }[] = [];
  const sorted = Array.from(seenContent).sort((a, b) => a - b);
  sorted.forEach((mc) => {
    const categoryKey = MESSAGE_CONTENT_TO_KEY[mc];
    if (!categoryKey) return;
    entries.push({ mc, categoryKey, base: baseByType.get(mc) ?? 0 });
  });

  const isMcIntraFlagged = (mc: number): boolean =>
    typeof intraContent === "boolean" ? intraContent : Boolean(intraContent[mc]);

  // Split entries by their individual intra-content flag. The flagged set
  // merges into one column; unflagged categories keep their own columns.
  // This replaces the legacy "group-by-shared-base" rule, which collapsed
  // unrelated categories when every base was forced to 0 above.
  const flaggedEntries = entries.filter((e) => isMcIntraFlagged(e.mc));
  const unflaggedEntries = entries.filter((e) => !isMcIntraFlagged(e.mc));

  // For each grouped category, infer a per-car delta when its member keys are
  // units-based but the persisted offset_mapping pair is delta=0. Without this
  // fallback, every car in the bank matrix renders at the same byte and the
  // cross-lift bleed validator (correctly) flags the collision.
  const groupedDeltaByMc = new Map<number, number>();
  normalKeys.forEach((k: any) => {
    const mc = k.messageContent ?? k.message_content;
    if (!mc || mc <= 0) return;
    const isUnits = k.unitsBased ?? k.units_based ?? false;
    if (!isUnits) return;
    const byteOffset = k.byteOffset ?? k.byte_offset ?? 0;
    const keySize = Math.max(byteOffset + 1, 1);
    const prev = groupedDeltaByMc.get(mc) ?? 0;
    if (keySize > prev) groupedDeltaByMc.set(mc, keySize);
  });

  // Per-category group_shared: true when any member key has the operator's
  // group_shared toggle set. Left absent (not false) otherwise, so the overlap
  // detector's category default (isGroupScopedCategory) still shares the
  // inherently group-scoped Group Supervisory Status / Group Control columns.
  const groupSharedByMc = new Set<number>();
  normalKeys.forEach((k: any) => {
    const mc = k.messageContent ?? k.message_content;
    if (mc && mc > 0 && (k.groupShared ?? k.group_shared) === true) {
      groupSharedByMc.add(mc);
    }
  });

  const categoryColumns: MappedMessageType[] = [];
  // Flagged categories merge only when they share BOTH the same initial offset
  // AND the same car delta. Without this, intra-content rolled every flagged
  // category into one column even when their persisted offsets had nothing in
  // common, so the merged matrix cell could not represent any of them
  // faithfully. Grouping by (initial, delta) keeps semantically related
  // categories merged while letting independent ones render distinctly.
  if (flaggedEntries.length > 0) {
    const flaggedGroups = new Map<string, typeof flaggedEntries>();
    flaggedEntries.forEach((e) => {
      const pair = initialDeltaByMc.get(e.mc) ?? [0, 0];
      const key = `${pair[0]}:${pair[1]}`;
      const bucket = flaggedGroups.get(key);
      if (bucket) {
        bucket.push(e);
      } else {
        flaggedGroups.set(key, [e]);
      }
    });
    flaggedGroups.forEach((group) => {
      const catKeys = group.map((e) => e.categoryKey).sort();
      const labels = group.map((e) => CATEGORY_LABELS[e.categoryKey] || e.categoryKey).sort();
      const groupedDelta = group.reduce(
        (acc, e) => Math.max(acc, groupedDeltaByMc.get(e.mc) ?? 0),
        0,
      );
      categoryColumns.push({
        label: labels.join(" + "),
        categoryKey: catKeys[0],
        categoryKeys: catKeys,
        baseAddress: 0,
        floorsBased: false,
        bitsPerRegister: 8,
        ...(groupedDelta > 0 ? { groupedUnitsBasedDelta: groupedDelta } : {}),
        ...(group.some((e) => groupSharedByMc.has(e.mc)) ? { groupShared: true } : {}),
      });
    });
  }
  unflaggedEntries.forEach((e) => {
    const groupedDelta = groupedDeltaByMc.get(e.mc) ?? 0;
    categoryColumns.push({
      label: CATEGORY_LABELS[e.categoryKey] || e.categoryKey,
      categoryKey: e.categoryKey,
      categoryKeys: [e.categoryKey],
      baseAddress: e.base,
      floorsBased: false,
      bitsPerRegister: 8,
      ...(groupedDelta > 0 ? { groupedUnitsBasedDelta: groupedDelta } : {}),
      ...(groupSharedByMc.has(e.mc) ? { groupShared: true } : {}),
    });
  });

  // Build own columns for bank_level + individual keys. bankLevel propagates
  // onto the MappedMessageType so downstream matrix renderers can disable
  // Car Delta for bank-level columns while keeping it editable for
  // individual (per-car) columns.
  // Deduplicated by column key: several keys can resolve to the same own
  // column (the same message mapped more than once, e.g. a duplicated key
  // set), and one column must render once. Grouped categories above already
  // dedupe via `seenContent`; without the same guard here every extra key
  // added another identical column to the matrix.
  //
  // bank_level wins when contributing keys disagree, matching the schema's
  // documented precedence (bank_level = own column, no per-car delta).
  const individualColumns: MappedMessageType[] = [];
  const individualByColumnKey = new Map<string, MappedMessageType>();
  ownColumnKeys.forEach((k) => {
    const mc: number = k.messageContent ?? k.message_content ?? 0;
    const catKey = MESSAGE_CONTENT_TO_KEY[mc] || String(mc);
    const ct = k.contentType ?? k.content_type;
    const isByte = ct === 2;
    const fb = k.floorsBased ?? k.floors_based ?? false;
    const bl = k.bankLevel ?? k.bank_level ?? false;
    const columnKey = individualColumnKey(k);

    const existing = individualByColumnKey.get(columnKey);
    if (existing) {
      if (bl) existing.bankLevel = true;
      return;
    }

    const column: MappedMessageType = {
      label: individualKeyLabel(k),
      categoryKey: columnKey,
      categoryKeys: [catKey],
      baseAddress: baseByType.get(mc) ?? 0,
      floorsBased: fb,
      bitsPerRegister: 8,
      isByteExpansion: isByte && fb,
      isIndividual: true,
      bankLevel: bl,
    };
    individualByColumnKey.set(columnKey, column);
    individualColumns.push(column);
  });

  const all = [...categoryColumns, ...individualColumns];
  // Sort columns alphabetically by label for consistent presentation.
  all.sort((a, b) => a.label.localeCompare(b.label));
  return all;
}

/**
 * Detect register overlaps between expanded mappings.
 *
 * Uses the adapter's individual key positions (byte + bit) for precise
 * collision detection.
 *
 * - When `intraContent` is OFF: two keys from different MessageContent types
 *   sharing the same absolute byte address is an overlap.
 * - When `intraContent` is ON: only flag if they share the same byte AND bit.
 *
 * When `categoryToRegType` is provided (Modbus adapters), keys/ranges whose
 * LN2 categories resolve to different Modbus register types (e.g., Coil vs
 * Input Register) are treated as non-colliding — each register type is an
 * independent Modbus address space.
 *
 * Returns a map of (liftIndex, categoryKey) → overlap description string.
 */
export function detectOverlaps(
  mappedTypes: MappedMessageType[],
  offsets: Record<string, number>[],
  _floorCount: number,
  intraContent: boolean,
  adapterKeys: AdapterKeyInfo[],
  disabledColumns: Set<string> = new Set(),
  categoryToRegType?: Record<string, string>,
  // Optional and last so every existing caller and test is unaffected. Only
  // used to derive each column's direction; absent means every column derives
  // `undefined`, which is exactly the pre-direction behaviour.
  ln1KeyMappings: MatrixKeyMappingSubset = [],
  // Optional and last, same reasoning: absent means the caller's any-true
  // boolean still governs, so no existing caller or test changes behaviour.
  intraContentFlags?: Partial<Record<number | string, boolean[]>>,
): Map<string, string> {
  // Accumulate OverlapInfo[] per output key so multiple collisions all surface.
  const infoMap = new Map<string, OverlapInfo[]>();

  // Individual bank_level columns use composite keys ("control:normal:5") —
  // fall back to the parsed category prefix when the full key isn't mapped.
  const resolveRegType = (categoryKey: string): string | undefined => {
    if (!categoryToRegType) return undefined;
    const direct = categoryToRegType[categoryKey];
    if (direct) return direct;
    const parsed = parseCompositeKey(categoryKey);
    return parsed ? categoryToRegType[parsed.categoryKey] : undefined;
  };

  const pushOverlap = (key: string, info: OverlapInfo): void => {
    const arr = infoMap.get(key);
    if (arr) {
      arr.push(info);
    } else {
      infoMap.set(key, [info]);
    }
  };

  // A spec carrying only what the direction excuse reads. Built through the
  // SAME `deriveColumnDirection` the range path and the adapter page use, so
  // the three cannot reach different verdicts about one key's direction.
  const directionSpec = (colKey: string): MatrixColumnSpec => ({
    colKey,
    label: colKey,
    size: 1,
    mcNumber: categoryKeyToMcNumber(colKey),
    direction: deriveColumnDirection(ln1KeyMappings, colKey),
  });

  if (adapterKeys.length > 0) {
    // -----------------------------------------------------------------------
    // Adapter-keys path: byte+bit–level collision detection.
    // Each AdapterKeyInfo represents one physical mapping entry with a
    // specific byte offset (and optional bit position) within the CDO.
    // We scan every lift independently since cross-lift bleed is not
    // a concern when the adapter defines individual byte positions.
    // -----------------------------------------------------------------------
    offsets.forEach((liftRow, liftIdx) => {
      const resolvedKeys: {
        absAddr: number;
        baseAddr: number;
        bit: number;
        categoryKey: string;
        regType: string | undefined;
        label: string;
      }[] = [];

      adapterKeys.forEach((k) => {
        const liftOffset = liftRow[k.categoryKey] ?? 0;
        resolvedKeys.push({
          absAddr: liftOffset + k.byteOffset,
          baseAddr: k.byteOffset,
          bit: k.bit,
          categoryKey: k.categoryKey,
          regType: resolveRegType(k.categoryKey),
          label: k.label,
        });
      });

      resolvedKeys.forEach((ka, i) => {
        resolvedKeys.slice(i + 1).forEach((kb) => {
          // Same MessageContent type — intra-type collisions are the adapter's responsibility
          if (ka.categoryKey === kb.categoryKey) return;
          // Different Modbus register types — independent address spaces
          if (ka.regType && kb.regType && ka.regType !== kb.regType) return;
          if (ka.absAddr !== kb.absAddr) return;

          // Opposite directions are not a collision -- one key writes the byte,
          // the other reads it, and they never contend.
          //
          // The range-based path below has applied this since direction landed;
          // this per-key path never did. It is reached for every NON-LN1
          // adapter (LN1-family adapters return no adapter keys precisely so
          // they take the range path), so a direction-split pair on a Modbus or
          // OPC adapter was excused on the adapter page and flagged here -- the
          // same two-screens-disagree bug the range path already fixed, just on
          // the other branch of the same `if`.
          if (
            columnDirectionsAreOpposed(directionSpec(ka.categoryKey), directionSpec(kb.categoryKey))
          )
            return;

          // Same byte address — check if it's a real collision
          const collision = intraContent ? ka.bit >= 0 && kb.bit >= 0 && ka.bit === kb.bit : true;

          // CDO-level coincidence: when two LN2 categories share BOTH the
          // same register type AND the same pre-offset CDO base address,
          // the overlap is defined by the adapter's CDO (one register
          // serving multiple interpretations), not caused by bank offsets.
          // Limit the skip to the range-based path (bit === -1) to preserve
          // bit-level collision detection on adapter-key bitmask mappings.
          const cdoCoincidence =
            ka.regType !== undefined &&
            ka.regType === kb.regType &&
            ka.baseAddr === kb.baseAddr &&
            ka.bit === -1 &&
            kb.bit === -1;

          if (!collision || cdoCoincidence) return;

          const key1 = `${liftIdx}:${ka.categoryKey}`;
          const key2 = `${liftIdx}:${kb.categoryKey}`;

          let overlappingBits: number[] | undefined;
          if (ka.bit >= 0 && kb.bit >= 0) {
            overlappingBits = ka.bit === kb.bit ? [ka.bit] : [ka.bit, kb.bit];
          }

          const infoAB: OverlapInfo = {
            a: {
              unitIdx: liftIdx,
              absByte: ka.absAddr,
              bit: ka.bit,
              colKey: ka.categoryKey,
              label: ka.label,
              size: 1,
              mcNumber: categoryKeyToMcNumber(ka.categoryKey),
            },
            b: {
              unitIdx: liftIdx,
              absByte: kb.absAddr,
              bit: kb.bit,
              colKey: kb.categoryKey,
              label: kb.label,
              size: 1,
              mcNumber: categoryKeyToMcNumber(kb.categoryKey),
            },
            overlappingBytes: [ka.absAddr],
            overlappingBits,
            intraAllowed: false,
            crossUnit: false,
          };

          pushOverlap(key1, infoAB);
          pushOverlap(key2, infoAB);
        });
      });
    });
  } else {
    // -----------------------------------------------------------------------
    // Range-based path: delegate to the shared detectBankMatrixOverlaps
    // primitive, which handles both intra-lift and cross-lift bleed.
    //
    // The bank lift offsets are additive on top of each column's CDO
    // baseAddress. We pre-compute adjusted absolute offsets so the shared
    // module works in the same absolute address space as the adapter's CDO.
    //
    // CDO-level coincidences (two LN2 categories sharing the same CDO
    // register under the same register type) are filtered post-hoc so that
    // the user is not presented with a false-positive they cannot resolve
    // via bank-level offsets.
    // -----------------------------------------------------------------------

    // Build column specs from enabled mapped types.
    const columnSpecs: MatrixColumnSpec[] = mappedTypes
      .filter((mt) => !disabledColumns.has(mt.categoryKey))
      .map((mt) => ({
        colKey: mt.categoryKey,
        label: mt.label,
        // Sized by the column's REAL byte span when the LN1 key mappings are
        // available, exactly as the adapter page sizes it
        // (AdapterOffsetMatrix's buildMatrixColumnSpecs). computeRegCount
        // returns a hardcoded 1 for these columns, because extractLn1MappedTypes
        // sets floorsBased:false on every LN1 grouped category — so a Control
        // column genuinely spanning bytes 0..40 was compared as [0,1) here and
        // [0,41) there, and the two pages disagreed about whether it collided
        // with anything in between. Falls back to the old count when no
        // mappings were supplied, so other callers are unchanged.
        size: ln1KeyMappings.length
          ? Math.max(1, ...mappedTypeSpans(mt, ln1KeyMappings, _floorCount ?? 0))
          : computeRegCount(mt, _floorCount),
        mcNumber: categoryKeyToMcNumber(mt.categoryKey),
        bankLevel: mt.bankLevel === true,
        isByteExpansion: mt.isByteExpansion === true,
        groupShared: mt.groupShared,
        regType: resolveRegType(mt.categoryKey),
        // Derived exactly as the adapter matrix derives it, from the same
        // helper, so the two screens cannot reach different verdicts about the
        // same keys. Undefined when the column has mixed directions, no keys,
        // or any key at BOTH -- in all of which the column still claims both
        // maps and the overlap stays flagged, so this is self-gating: with no
        // key carrying a direction, detection is byte-identical to before.
        direction: deriveColumnDirection(ln1KeyMappings, mt.categoryKey),
      }));

    // Map colKey → CDO baseAddress for CDO-coincidence post-filtering.
    const baseAddressMap: Record<string, number> = {};
    mappedTypes.forEach((mt) => {
      baseAddressMap[mt.categoryKey] = mt.baseAddress;
    });

    // Build per-lift absolute offsets: baseAddress + bank lift offset.
    const adjustedOffsets: Record<string, number>[] = offsets.map((liftRow) => {
      const adjusted: Record<string, number> = {};
      columnSpecs.forEach((col) => {
        adjusted[col.colKey] = (baseAddressMap[col.colKey] ?? 0) + (liftRow[col.colKey] ?? 0);
      });
      return adjusted;
    });

    // Per-category when the adapter's flags are available, matching the adapter
    // page's `(mcN, unitIdx) => intraLookup(flags, mcN, unitIdx)`. The detector
    // then requires BOTH participants to be flagged before it excuses a pair.
    //
    // Falls back to the any-true boolean only when no flags were supplied, so
    // this cannot change behaviour for a caller that does not pass them.
    const intraFn: IntraContentLookup = intraContentFlags
      ? (mcNumber: number, unitIdx: number) => intraLookup(intraContentFlags, mcNumber, unitIdx)
      : () => intraContent;

    const bleedMap = detectBankMatrixOverlaps(
      columnSpecs,
      adjustedOffsets,
      intraFn,
      categoryToRegType,
      undefined,
      "always-flag",
      // Lets the detector fall back to a per-BYTE direction test when a whole
      // category column derives no single direction of its own, and refine a
      // range overlap down to the bytes the columns' messages actually claim.
      ln1KeyMappings,
      _floorCount,
    );

    // Translate keys from "colKey:liftIdx" (shared module format) to
    // "liftIdx:colKey" (BankOffsetMatrix format) and filter CDO coincidences.
    bleedMap.forEach((infos, sharedKey) => {
      // Derive the output key by reversing "colKey:liftIdx".
      // colKey may contain colons (composite keys like "control:normal:5"),
      // so we find the last colon which separates the liftIdx suffix.
      const lastColon = sharedKey.lastIndexOf(":");
      if (lastColon === -1) return;
      const colKey = sharedKey.slice(0, lastColon);
      const liftIdxStr = sharedKey.slice(lastColon + 1);
      const outputKey = `${liftIdxStr}:${colKey}`;

      infos.forEach((info) => {
        // CDO-level coincidence guard (range-based path only):
        // when two columns share the same CDO base address AND the same
        // register type, their collision is a CDO-defined fan-out that
        // bank offsets cannot resolve — do not flag it.
        const aBase = baseAddressMap[info.a.colKey] ?? -1;
        const bBase = baseAddressMap[info.b.colKey] ?? -1;
        const aRegType = resolveRegType(info.a.colKey);
        const bRegType = resolveRegType(info.b.colKey);
        const isCdoCoincidence =
          aBase === bBase && aBase >= 0 && aRegType !== undefined && aRegType === bRegType;
        if (isCdoCoincidence) return;

        pushOverlap(outputKey, info);
      });
    });
  }

  // Convert accumulated OverlapInfo[] per key to formatted tooltip strings.
  const overlaps = new Map<string, string>();
  infoMap.forEach((infos, key) => {
    if (infos.length === 0) return;
    overlaps.set(key, infos.map(formatOverlapTooltip).join(" | "));
  });

  return overlaps;
}

/**
 * Detect which cells deviate from the auto-stride pattern.
 * The stride is computed from rows 0 and 1 per column. Any cell in rows 2+
 * whose value differs from the expected stride-based value is flagged.
 * Returns a map of "liftIdx:categoryKey" → expected value.
 */
function detectStrideDeviations(
  offsets: Record<string, number>[],
  columnKeys: string[],
): Map<string, number> {
  const deviations = new Map<string, number>();
  if (offsets.length < 3 || columnKeys.length === 0) return deviations;

  columnKeys.forEach((col) => {
    const stride = (offsets[1]?.[col] ?? 0) - (offsets[0]?.[col] ?? 0);
    for (let row = 2; row < offsets.length; row += 1) {
      const expected = (offsets[1]?.[col] ?? 0) + stride * (row - 1);
      const actual = offsets[row]?.[col] ?? 0;
      if (actual !== expected) {
        deviations.set(`${row}:${col}`, expected);
      }
    }
  });

  return deviations;
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Simplified-mode helper (pure, exported for testing)
// ---------------------------------------------------------------------------

/**
 * Compute full offset matrix from simplified per-lift base offsets.
 * Iterates over mappedTypes (source of truth for columns) — NOT over
 * adapterDefaults keys — so missing adapter defaults safely default to 0.
 */
export function computeSimplifiedOffsets(
  baseOffsets: number[],
  adapterDefaults: Record<string, number>[] | undefined,
  mappedTypes: MappedMessageType[],
): Record<string, number>[] {
  return baseOffsets.map((base, i) => {
    const row: Record<string, number> = {};
    mappedTypes.forEach((mt) => {
      row[mt.categoryKey] = Math.max(0, (adapterDefaults?.[i]?.[mt.categoryKey] ?? 0) + base);
    });
    return row;
  });
}

export function deriveSimplifiedBaseOffsets(
  offsets: Record<string, number>[],
  adapterDefaults: Record<string, number>[] | undefined,
  mappedTypes: MappedMessageType[],
  disabledColumns: Set<string> = new Set(),
): number[] | null {
  if (offsets.length === 0) return [];

  const activeKeys = mappedTypes
    .filter((mt) => !disabledColumns.has(mt.categoryKey))
    .map((mt) => mt.categoryKey);

  const derived = offsets.map((row, liftIdx) => {
    const keysToCheck = activeKeys.length > 0 ? activeKeys : Object.keys(row || {});
    let baseOffset: number | null = null;

    const isValid = keysToCheck.every((categoryKey) => {
      const currentValue = row?.[categoryKey] ?? 0;
      const defaultValue = adapterDefaults?.[liftIdx]?.[categoryKey] ?? 0;
      const delta = currentValue - defaultValue;

      if (delta < 0) {
        return false;
      }

      if (baseOffset === null) {
        baseOffset = delta;
        return true;
      }

      return delta === baseOffset;
    });

    if (!isValid) {
      return null;
    }

    return Math.max(0, baseOffset ?? 0);
  });

  return derived.every((value): value is number => value !== null) ? derived : null;
}

// Component
// ---------------------------------------------------------------------------
export default function BankOffsetMatrix({
  lifts,
  mappedTypes,
  offsets,
  onOffsetsChange,
  floorCount,
  intraContent,
  adapterKeys,
  // Defaulted rather than left undefined: an adapter with no LN1 key mappings
  // derives `undefined` for every column, which the detector treats as "claims
  // both maps" and keeps flagging — the pre-direction behaviour exactly.
  ln1KeyMappings = [],
  intraContentFlags = undefined,
  onOverlapChange,
  disabledColumns = new Set(),
  onDisabledColumnsChange = undefined,
  adapterDefaults = undefined,
  initialSimpleBaseOffsets = null,
  registerTypeMappedTypes = undefined,
  categoryToRegType = undefined,
  adapterDefaults2Row = undefined,
}: BankOffsetMatrixProps) {
  const [autoStride, setAutoStride] = useState(true);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [isSimplified, setIsSimplified] = useState(false);
  const [simpleBaseOffsets, setSimpleBaseOffsets] = useState<number[]>(() =>
    Array(lifts.length).fill(0),
  );
  const [byRegisterType, setByRegisterType] = useState(
    () => !!(registerTypeMappedTypes && registerTypeMappedTypes.length > 0),
  );

  // Reset simplified mode to OFF whenever the parent loads a new bank/edit
  // state. Simplified is always default off; when saved base offsets are
  // provided we preload them so toggling Simplified ON manually reuses the
  // persisted values rather than recomputing from zero.
  useEffect(() => {
    setExcludedRows(new Set());
    setByRegisterType(!!(registerTypeMappedTypes && registerTypeMappedTypes.length > 0));
    setIsSimplified(false);
    if (initialSimpleBaseOffsets && initialSimpleBaseOffsets.length > 0) {
      setSimpleBaseOffsets(lifts.map((_, idx) => initialSimpleBaseOffsets[idx] ?? 0));
      return;
    }
    setSimpleBaseOffsets(Array(lifts.length).fill(0));
  }, [initialSimpleBaseOffsets, registerTypeMappedTypes]);

  // Resize simpleBaseOffsets array when lifts.length changes
  const liftsLength = lifts.length;
  useEffect(() => {
    setSimpleBaseOffsets((prev) => {
      if (prev.length === liftsLength) return prev;
      return lifts.map((_, i) => {
        if (i < prev.length) return prev[i];
        if (prev.length >= 2) {
          const stride = prev[1] - prev[0];
          return Math.max(0, prev[1] + stride * (i - 1));
        }
        return 0;
      });
    });
  }, [liftsLength]); // lifts.length change only; prev accessed via functional setter

  // When byRegisterType is on, use the register-type-grouped columns instead
  // of the LN2 category columns for rendering, overlap detection, and stride.
  const activeMappedTypes =
    byRegisterType && registerTypeMappedTypes?.length ? registerTypeMappedTypes : mappedTypes;

  // Number of cars for per-cell derived-value computation
  const numCars = lifts.length;

  // Build effective offsets: merge stored values with adapter-derived defaults.
  // A cell uses the stored value if present; otherwise falls back to the
  // derived value (initial + liftIdx * delta). This is fed into detectOverlaps
  // so overlap detection works on effective values, not raw stored values.
  const effectiveOffsets: Record<string, number>[] = useMemo(
    () =>
      offsets.map((liftDict, liftIdx) => {
        const merged: Record<string, number> = {};
        // Iterate over EVERY type the matrix could render — LN2-keyed
        // mappedTypes for the default mode AND register-type-keyed
        // registerTypeMappedTypes for byRegisterType mode. detectOverlaps
        // receives `activeMappedTypes` as its first argument and looks up
        // `liftRow[col.colKey]` against THAT key set, so the merged object
        // needs every potential colKey populated — otherwise a MODBUS
        // bank running in byRegisterType mode reads `liftRow.write_register`
        // as undefined → 0 → cross-lift bleed false positive even when
        // the actual stored offsets are unique per lift.
        const fillFor = (t: MappedMessageType): void => {
          const key = t.categoryKey;
          if (key in merged) return;
          const defaults = adapterDefaults2Row?.[key];
          if (liftDict[key] !== undefined) {
            merged[key] = liftDict[key];
          } else if (defaults) {
            // Fall back to the inferred grouped-units-based delta when the
            // adapter persisted delta=0 for a grouped category whose member
            // keys are units-based. Without this, all lifts derive to the
            // same byte and the validator (correctly) reports cross-lift
            // bleed even though each car in fact owns a separate register.
            const delta =
              defaults.delta === 0 && t.groupedUnitsBasedDelta
                ? t.groupedUnitsBasedDelta
                : defaults.delta;
            merged[key] = defaults.initial + liftIdx * delta;
          }
        };
        mappedTypes.forEach(fillFor);
        registerTypeMappedTypes?.forEach(fillFor);
        return merged;
      }),
    [offsets, mappedTypes, registerTypeMappedTypes, adapterDefaults2Row, numCars],
  );

  // Filter adapter keys for disabled columns (keys are matched by
  // categoryKey, not positional index, so filtering is safe).
  const enabledKeys = useMemo(
    () => adapterKeys.filter((k) => !disabledColumns.has(k.categoryKey)),
    [adapterKeys, disabledColumns],
  );

  const overlaps = useMemo(
    () =>
      detectOverlaps(
        // Pass the active mapped types array — detectOverlaps uses colIdx to
        // look up offsets[liftIdx][colIdx], so the array must match the
        // offsets matrix column order. Disabled columns are skipped below.
        activeMappedTypes,
        effectiveOffsets,
        floorCount,
        intraContent,
        // In By Register Type mode the user is deliberately grouping every
        // LN2 category sharing a register type under one offset, so the
        // per-LN2-category adapterKeys produce false-positive collisions
        // (all keys fan out to the same offset and then "collide" at the
        // shared position). Skip them so detectOverlaps falls back to the
        // range-based check on the register-type-level mapped types, which
        // correctly flags only cross-register-type absolute collisions.
        byRegisterType ? [] : enabledKeys,
        disabledColumns,
        // Cross-register-type keys can never collide (different Modbus
        // function codes address independent register spaces), so pass the
        // mapping through to detectOverlaps whenever the adapter provides it.
        categoryToRegType,
        // Lets the range-based path excuse a Control/Traffic pair that shares
        // one byte in OPPOSITE directions -- legal by design, and what the
        // adapter matrix already excused. Without it the two screens disagreed.
        ln1KeyMappings,
        intraContentFlags,
      ),
    [
      activeMappedTypes,
      effectiveOffsets,
      floorCount,
      intraContent,
      byRegisterType,
      enabledKeys,
      disabledColumns,
      categoryToRegType,
      ln1KeyMappings,
      intraContentFlags,
    ],
  );

  const strideDeviations = useMemo(() => {
    if (!autoStride) return new Map<string, number>();
    const colKeys = activeMappedTypes.map((mt) => mt.categoryKey);
    const devs = detectStrideDeviations(offsets, colKeys);
    // Remove deviations for excluded rows
    excludedRows.forEach((row) => {
      colKeys.forEach((col) => {
        devs.delete(`${row}:${col}`);
      });
    });
    return devs;
  }, [autoStride, offsets, activeMappedTypes.length, excludedRows, byRegisterType]);

  const hasOverlaps = overlaps.size > 0;
  const hasDeviations = strideDeviations.size > 0;

  // Notify parent of overlap state changes
  useEffect(() => {
    if (onOverlapChange) onOverlapChange(hasOverlaps);
  }, [hasOverlaps, onOverlapChange]);

  const handleCellChange = useCallback(
    (liftIdx: number, categoryKey: string, value: number | null) => {
      const updated = offsets.map((row) => ({ ...row }));
      if (!updated[liftIdx]) {
        updated[liftIdx] = {};
      }
      const v = value ?? 0;
      const mt = activeMappedTypes.find((m) => m.categoryKey === categoryKey);
      // Fan out writes to every member of a merged (intra-content) column so
      // every participating MessageContent stays in sync. `categoryKeys` has
      // one element for normal columns, so single-member writes are unchanged.
      const targetKeys = mt && mt.categoryKeys.length > 1 ? mt.categoryKeys : [categoryKey];
      targetKeys.forEach((ck) => {
        updated[liftIdx][ck] = v;
      });

      // For non-individual columns (bank-wide categories): when editing
      // Car 1 (row 0), auto-fill the entire column so all lifts get
      // the same offset. The user can then override individual lifts
      // by editing their cells directly (rows 1+). This gives "type
      // once, fills all" behavior for bank-level offsets.
      if (mt && !mt.isIndividual && liftIdx === 0 && !autoStride) {
        for (let r = 1; r < updated.length; r += 1) {
          if (!updated[r]) updated[r] = {};
          targetKeys.forEach((ck) => {
            updated[r][ck] = v;
          });
        }
      }

      // When auto-stride is ON and editing row 0 or 1 with 3+ rows,
      // auto-fill non-excluded rows 2+ by inferring per-column stride.
      // For merged (intra-content) columns, expand each column's stride to
      // all member keys so every grouped MessageContent stays coherent.
      if (autoStride && liftIdx <= 1 && updated.length >= 3) {
        const columnTargets = activeMappedTypes.map((m) => ({
          ck: m.categoryKey,
          members: m.categoryKeys.length > 1 ? m.categoryKeys : [m.categoryKey],
        }));
        const strides: Record<string, number> = {};
        columnTargets.forEach(({ ck }) => {
          strides[ck] = updated.length >= 2 ? (updated[1][ck] ?? 0) - (updated[0][ck] ?? 0) : 0;
        });
        for (let r = 2; r < updated.length; r += 1) {
          if (!excludedRows.has(r)) {
            columnTargets.forEach(({ ck, members }) => {
              const next = (updated[1][ck] ?? 0) + strides[ck] * (r - 1);
              members.forEach((member) => {
                updated[r][member] = next;
              });
            });
          }
        }
      }

      // When in register-type mode, expand register-type offsets to all
      // underlying LN2 categories so the parent's offset state stays coherent.
      if (byRegisterType && categoryToRegType && registerTypeMappedTypes) {
        const regTypeEntry = registerTypeMappedTypes.find((rmt) => rmt.categoryKey === categoryKey);
        if (regTypeEntry) {
          for (let r = 0; r < updated.length; r += 1) {
            const regVal = updated[r][categoryKey] ?? 0;
            regTypeEntry.categoryKeys.forEach((ck) => {
              updated[r][ck] = regVal;
            });
          }
        }
      }

      onOffsetsChange(updated);
    },
    [
      autoStride,
      excludedRows,
      offsets,
      onOffsetsChange,
      activeMappedTypes,
      byRegisterType,
      categoryToRegType,
      registerTypeMappedTypes,
    ],
  );

  const handleSimpleCellChange = useCallback(
    (liftIdx: number, value: number | null) => {
      const newBase = value ?? 0;
      setSimpleBaseOffsets((prev) => {
        const updated = [...prev];
        updated[liftIdx] = newBase;

        // Auto-stride: if editing lift 0 or 1 with >= 3 lifts, fill 2+
        if (autoStride && liftIdx <= 1 && updated.length >= 3) {
          const stride = updated[1] - updated[0];
          for (let r = 2; r < updated.length; r += 1) {
            if (!excludedRows.has(r)) {
              updated[r] = Math.max(0, updated[1] + stride * (r - 1));
            }
          }
        }

        // Fan out to full offsets matrix synchronously in the same setter
        onOffsetsChange(computeSimplifiedOffsets(updated, adapterDefaults, mappedTypes));
        return updated;
      });
    },
    [autoStride, excludedRows, adapterDefaults, mappedTypes, onOffsetsChange],
  );

  const handleReset = useCallback(() => {
    if (isSimplified) {
      const zeroed = Array(lifts.length).fill(0);
      setSimpleBaseOffsets(zeroed);
      onOffsetsChange(computeSimplifiedOffsets(zeroed, adapterDefaults, mappedTypes));
      return;
    }

    // Clear all overrides so cells fall back to the adapter's
    // initial+delta defaults (computed by `effectiveOffsets` from
    // adapterDefaults2Row). Empty `{}` rows have no keys → every cell
    // re-derives as `initial + liftIdx * delta` per column.
    const clearedMatrix: Record<string, number>[] = offsets.map(() => ({}));
    onOffsetsChange(clearedMatrix);
  }, [isSimplified, lifts.length, offsets, onOffsetsChange, adapterDefaults, mappedTypes]);

  // Force overlap/stride detection to re-evaluate the current offset values.
  // Useful when the UI appears out of sync after toggling modes or clearing
  // values. In By Register Type mode, also re-fans each register-type offset
  // to its claimed LN2 category keys so the underlying per-category state
  // stays coherent with what the user sees in the matrix.
  const handleRefresh = useCallback(() => {
    const normalized = offsets.map((row) => {
      const copy: Record<string, number> = { ...row };
      if (byRegisterType && registerTypeMappedTypes?.length) {
        registerTypeMappedTypes.forEach((rmt) => {
          const regVal = copy[rmt.categoryKey] ?? 0;
          rmt.categoryKeys.forEach((ck) => {
            copy[ck] = regVal;
          });
        });
      }
      return copy;
    });
    onOffsetsChange(normalized);
  }, [offsets, byRegisterType, registerTypeMappedTypes, onOffsetsChange]);

  const handleEnterSimplified = useCallback(
    (on: boolean) => {
      setByRegisterType(false);
      if (on) {
        // Derive base offsets from current full matrix — min delta per lift
        const activeKeys = mappedTypes
          .filter((mt) => !disabledColumns.has(mt.categoryKey))
          .map((mt) => mt.categoryKey);
        const derived = lifts.map((_, i) => {
          if (activeKeys.length === 0) return 0;
          const liftRow = offsets[i] || {};
          let minDelta = Infinity;
          activeKeys.forEach((cat) => {
            const delta = (liftRow[cat] ?? 0) - (adapterDefaults?.[i]?.[cat] ?? 0);
            if (delta < minDelta) minDelta = delta;
          });
          return Math.max(0, Number.isFinite(minDelta) ? minDelta : 0);
        });
        setSimpleBaseOffsets(derived);
        setExcludedRows(new Set());
        onOffsetsChange(computeSimplifiedOffsets(derived, adapterDefaults, mappedTypes));
      }
      setIsSimplified(on);
    },
    [lifts, offsets, adapterDefaults, mappedTypes, disabledColumns, onOffsetsChange],
  );

  const handleToggleRegisterType = useCallback(
    (on: boolean) => {
      if (on && categoryToRegType && registerTypeMappedTypes?.length) {
        // Convert from per-category offsets to per-register-type offsets.
        // For each register type, take the offset of its first category.
        const newOffsets = offsets.map((row) => {
          const regRow: Record<string, number> = {};
          registerTypeMappedTypes.forEach((rmt) => {
            // Take the first category's offset as the register type offset
            const firstCat = rmt.categoryKeys.find((ck) => row[ck] !== undefined);
            regRow[rmt.categoryKey] = firstCat ? (row[firstCat] ?? 0) : 0;
          });
          return regRow;
        });
        onOffsetsChange(newOffsets);
      } else if (!on && categoryToRegType && registerTypeMappedTypes?.length) {
        // Convert from per-register-type offsets back to per-category offsets.
        // Apply each register type's offset to all its categories.
        const newOffsets = offsets.map((row) => {
          const catRow: Record<string, number> = {};
          mappedTypes.forEach((mt) => {
            const regType = categoryToRegType[mt.categoryKey];
            catRow[mt.categoryKey] = regType ? (row[regType] ?? 0) : (row[mt.categoryKey] ?? 0);
          });
          return catRow;
        });
        onOffsetsChange(newOffsets);
      }
      setByRegisterType(on);
    },
    [offsets, onOffsetsChange, mappedTypes, registerTypeMappedTypes, categoryToRegType],
  );

  // ---------------------------------------------------------------------------
  // Per-cell helpers for adapter-derived default support
  // ---------------------------------------------------------------------------

  /** Resolve the set of underlying category keys for a rendered column. For
   *  merged (intra-content) columns this returns every member so callers can
   *  read/write each. For normal columns this returns just `[categoryKey]`. */
  function resolveMemberKeys(categoryKey: string): string[] {
    const mt = activeMappedTypes.find((m) => m.categoryKey === categoryKey);
    return mt && mt.categoryKeys.length > 1 ? mt.categoryKeys : [categoryKey];
  }

  /** Compute the adapter-derived value for a specific lift and category.
   *  For merged columns, take `max` across member initials/deltas so a stale
   *  non-zero value on any member remains visible until the next save
   *  reconciles all members — this surfaces divergence instead of hiding it
   *  behind the alphabetically-first member. */
  function computeDerivedValue(liftIdx: number, categoryKey: string): number | undefined {
    const memberKeys = resolveMemberKeys(categoryKey);
    let maxInitial = Number.NEGATIVE_INFINITY;
    let maxDelta = Number.NEGATIVE_INFINITY;
    memberKeys.forEach((ck) => {
      const d = adapterDefaults2Row?.[ck];
      if (!d) return;
      if (d.initial > maxInitial) maxInitial = d.initial;
      if (d.delta > maxDelta) maxDelta = d.delta;
    });
    if (maxInitial === Number.NEGATIVE_INFINITY) return undefined;
    // Same fallback as effectiveOffsets: when the adapter recorded delta=0
    // for a grouped units-based category, derive a per-car stride from the
    // member key sizes so cars 2..N do not collide on car 1's byte.
    const mt = activeMappedTypes.find((m) => m.categoryKey === categoryKey);
    const effectiveDelta =
      maxDelta <= 0 && mt?.groupedUnitsBasedDelta ? mt.groupedUnitsBasedDelta : maxDelta;
    return expandOffsets(maxInitial, effectiveDelta, numCars)[liftIdx];
  }

  /** Compute the effective stored value for a cell, falling back to derived.
   *  Direct key hit wins — that covers both real user overrides and the
   *  pre-populated values from `dataSource` (where merged/register-type
   *  columns get their display value computed once). For multi-member
   *  columns without a direct hit, fall back to `max` across member keys so
   *  a stale non-zero stored value remains visible. */
  function computeStoredValue(
    liftRow: Record<string, number>,
    liftIdx: number,
    categoryKey: string,
  ): number {
    if (liftRow[categoryKey] !== undefined) return liftRow[categoryKey];
    const memberKeys = resolveMemberKeys(categoryKey);
    if (memberKeys.length > 1) {
      let hasOverride = false;
      let maxStored = Number.NEGATIVE_INFINITY;
      memberKeys.forEach((ck) => {
        if (ck === categoryKey) return;
        const v = liftRow[ck];
        if (v === undefined) return;
        hasOverride = true;
        if (v > maxStored) maxStored = v;
      });
      if (hasOverride) return maxStored;
    }
    const derived = computeDerivedValue(liftIdx, categoryKey);
    return derived ?? 0;
  }

  /** Get the classification for a cell: "derived" if stored matches adapter default, else "overridden". */
  function getCellClassification(
    stored: number,
    liftIdx: number,
    categoryKey: string,
  ): "derived" | "overridden" {
    const derived = computeDerivedValue(liftIdx, categoryKey);
    if (derived === undefined) return "overridden";
    return classifyBankCell(stored, derived);
  }

  /** Handle clearing an override: delete the key so next render recomputes
   *  from adapter. For merged columns, clear every member so the revert is
   *  complete. */
  function handleClearOverride(liftIdx: number, categoryKey: string): void {
    const updated = offsets.map((row) => ({ ...row }));
    if (!updated[liftIdx]) return;
    resolveMemberKeys(categoryKey).forEach((ck) => {
      delete updated[liftIdx][ck];
    });
    onOffsetsChange(updated);
  }

  // Build table columns: Lift Name + one column per MessageContent type
  const columns: ColumnsType<OffsetRow> = useMemo(() => {
    const cols: ColumnsType<OffsetRow> = [
      {
        title: "Lift",
        dataIndex: "liftName",
        key: "liftName",
        width: 120,
        fixed: "left",
        render: (name: string, record: OffsetRow) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {autoStride && record.liftIndex >= 2 && (
              <Tooltip
                title={
                  excludedRows.has(record.liftIndex)
                    ? "Include in auto-stride"
                    : "Exclude from auto-stride"
                }
              >
                <Checkbox
                  checked={!excludedRows.has(record.liftIndex)}
                  onChange={(e) => {
                    setExcludedRows((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) {
                        next.delete(record.liftIndex);
                      } else {
                        next.add(record.liftIndex);
                      }
                      return next;
                    });
                  }}
                  style={{ marginRight: 2 }}
                />
              </Tooltip>
            )}
            {name}
            <Tag
              color={excludedRows.has(record.liftIndex) ? "default" : "blue"}
              style={{ fontSize: 10 }}
            >
              #{record.liftIndex}
            </Tag>
            {excludedRows.has(record.liftIndex) && (
              <Tag color="orange" style={{ fontSize: 9 }}>
                excluded
              </Tag>
            )}
          </span>
        ),
      },
    ];

    activeMappedTypes.forEach((mt, colIdx) => {
      const isDisabled = disabledColumns.has(mt.categoryKey);
      cols.push({
        title: (
          <div
            data-testid={`column-header-${colIdx}`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Tooltip
              title={
                isDisabled
                  ? "Column disabled — not included in offsets or overlap detection"
                  : `Base addr: ${mt.baseAddress}${
                      mt.floorsBased ? ` (expands ${floorCount} floors)` : ""
                    }`
              }
            >
              <span
                style={isDisabled ? { opacity: 0.4, textDecoration: "line-through" } : undefined}
              >
                {mt.label}
                {mt.floorsBased && (
                  <Tag color="orange" style={{ fontSize: 9, marginLeft: 4 }}>
                    F
                  </Tag>
                )}
              </span>
            </Tooltip>
            {onDisabledColumnsChange && (
              <Checkbox
                data-testid={`column-toggle-${mt.categoryKey}`}
                checked={!isDisabled}
                onChange={(e) => {
                  const next = new Set(disabledColumns);
                  if (e.target.checked) {
                    next.delete(mt.categoryKey);
                  } else {
                    next.add(mt.categoryKey);
                  }
                  onDisabledColumnsChange(next);
                }}
                style={{ marginTop: 2 }}
              />
            )}
          </div>
        ),
        key: mt.categoryKey,
        width: 130,
        align: "center" as const,
        render: (_: unknown, record: OffsetRow) => {
          if (isDisabled) {
            return (
              <InputNumber
                data-testid={`matrix-cell-${record.liftIndex}-${mt.categoryKey}`}
                size="small"
                value={record.offsets[mt.categoryKey] ?? 0}
                disabled
                style={{ width: 100, opacity: 0.3 }}
              />
            );
          }

          const overlapKey = `${record.liftIndex}:${mt.categoryKey}`;
          const overlapMsg = overlaps.get(overlapKey);
          const deviationKey = `${record.liftIndex}:${mt.categoryKey}`;
          const expectedVal = strideDeviations.get(deviationKey);
          const hasDeviation = expectedVal !== undefined;
          const storedValue = computeStoredValue(record.offsets, record.liftIndex, mt.categoryKey);
          const cellClassification = getCellClassification(
            storedValue,
            record.liftIndex,
            mt.categoryKey,
          );
          const isDerived = cellClassification === "derived";
          const derivedExists = adapterDefaults2Row?.[mt.categoryKey] !== undefined;
          const finalAddr = mt.baseAddress + storedValue;
          const regCount = computeRegCount(mt, floorCount);
          const endAddr = finalAddr + regCount;

          const tooltipParts: string[] = [];
          if (overlapMsg) tooltipParts.push(overlapMsg);
          if (hasDeviation) tooltipParts.push(`Breaks stride pattern (expected ${expectedVal})`);
          if (tooltipParts.length === 0) tooltipParts.push(`Registers [${finalAddr}, ${endAddr})`);

          let borderColor: string | undefined;
          let bgColor: string | undefined;
          let statusVal: "error" | "warning" | undefined;
          if (overlapMsg) {
            borderColor = "#ff4d4f";
            bgColor = "rgba(255, 77, 79, 0.1)";
            statusVal = "error";
          } else if (hasDeviation) {
            borderColor = "#faad14";
            bgColor = "rgba(250, 173, 20, 0.1)";
            statusVal = "warning";
          }

          const cellColor = isDerived ? "#888" : undefined;

          return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              <Tooltip title={tooltipParts.join(". ")}>
                <InputNumber
                  data-testid={`bank-offset-${record.liftIndex}-${mt.categoryKey}-${cellClassification}`}
                  size="small"
                  min={0}
                  max={65535}
                  value={storedValue}
                  onChange={(val) => handleCellChange(record.liftIndex, mt.categoryKey, val)}
                  style={{
                    width: 100,
                    borderColor,
                    backgroundColor: bgColor,
                    color: cellColor,
                  }}
                  status={statusVal}
                />
              </Tooltip>
              {!isDerived && derivedExists && (
                <button
                  data-testid={`bank-offset-clear-${record.liftIndex}-${mt.categoryKey}`}
                  type="button"
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    padding: "0 2px",
                    color: "#888",
                    fontSize: 12,
                    lineHeight: 1,
                  }}
                  onClick={() => handleClearOverride(record.liftIndex, mt.categoryKey)}
                >
                  &times;
                </button>
              )}
            </div>
          );
        },
      });
    });

    return cols;
  }, [
    activeMappedTypes,
    overlaps,
    strideDeviations,
    floorCount,
    handleCellChange,
    autoStride,
    excludedRows,
    disabledColumns,
    onDisabledColumnsChange,
    byRegisterType,
    adapterDefaults2Row,
    offsets,
    onOffsetsChange,
    numCars,
  ]);

  // Build table data rows
  const dataSource: OffsetRow[] = useMemo(
    () =>
      lifts.map((lift, idx) => {
        const rowOffsets: Record<string, number> = {};
        const liftRow = offsets[idx] || {};
        activeMappedTypes.forEach((mt) => {
          // Direct value at the active column's key wins.
          if (liftRow[mt.categoryKey] !== undefined) {
            rowOffsets[mt.categoryKey] = liftRow[mt.categoryKey];
            return;
          }
          // Fallback for multi-member columns (intra-content merged OR "By
          // Register Type" proxy): take `max` across any member with a
          // stored value. Intra-content merging uses max so a stale non-zero
          // member remains visible until the next save reconciles divergence;
          // register-type proxies naturally have equal values across members,
          // so max is a no-op in that case. (In single-member mode,
          // categoryKeys has one entry equal to categoryKey, so this
          // fallback is a no-op.)
          let hasMemberOverride = false;
          let memberMax = Number.NEGATIVE_INFINITY;
          mt.categoryKeys.forEach((ck) => {
            if (ck === mt.categoryKey) return;
            const v = liftRow[ck];
            if (v === undefined) return;
            hasMemberOverride = true;
            if (v > memberMax) memberMax = v;
          });
          if (hasMemberOverride) {
            rowOffsets[mt.categoryKey] = memberMax;
            return;
          }
          // Fallback: adapter-derived default for this lift + category.
          // For merged columns computeDerivedValue takes max across members.
          const derived = computeDerivedValue(idx, mt.categoryKey);
          if (derived !== undefined) {
            rowOffsets[mt.categoryKey] = derived;
            return;
          }
          rowOffsets[mt.categoryKey] = 0;
        });

        const rowOverlaps: Record<string, string> = {};
        activeMappedTypes.forEach((mt) => {
          const key = `${idx}:${mt.categoryKey}`;
          const msg = overlaps.get(key);
          if (msg) rowOverlaps[mt.categoryKey] = msg;
        });

        return {
          key: lift.id || String(idx),
          liftIndex: idx,
          liftName: lift.name || `Lift ${idx + 1}`,
          liftId: lift.id,
          offsets: rowOffsets,
          overlaps: rowOverlaps,
        };
      }),
    [lifts, activeMappedTypes, offsets, overlaps, byRegisterType, adapterDefaults2Row, numCars],
  );

  // Simplified-mode table data
  const simplifiedDataSource = useMemo(
    () =>
      lifts.map((lift, idx) => ({
        key: lift.id || String(idx),
        liftIndex: idx,
        liftName: lift.name || `Lift ${idx + 1}`,
      })),
    [lifts],
  );

  type SimplifiedRow = { key: string; liftIndex: number; liftName: string };

  const simplifiedColumns: ColumnsType<SimplifiedRow> = useMemo(
    () => [
      {
        title: "Lift",
        dataIndex: "liftName",
        key: "liftName",
        width: 160,
        render: (name: string, record: SimplifiedRow) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {autoStride && record.liftIndex >= 2 && (
              <Tooltip
                title={
                  excludedRows.has(record.liftIndex)
                    ? "Include in auto-stride"
                    : "Exclude from auto-stride"
                }
              >
                <Checkbox
                  checked={!excludedRows.has(record.liftIndex)}
                  onChange={(e) => {
                    setExcludedRows((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) {
                        next.delete(record.liftIndex);
                      } else {
                        next.add(record.liftIndex);
                      }
                      return next;
                    });
                  }}
                  style={{ marginRight: 2 }}
                />
              </Tooltip>
            )}
            {name}
            <Tag
              color={excludedRows.has(record.liftIndex) ? "default" : "blue"}
              style={{ fontSize: 10 }}
            >
              #{record.liftIndex}
            </Tag>
            {excludedRows.has(record.liftIndex) && (
              <Tag color="orange" style={{ fontSize: 9 }}>
                excluded
              </Tag>
            )}
          </span>
        ),
      },
      {
        title: "Base Offset",
        key: "baseOffset",
        width: 160,
        align: "center" as const,
        render: (_: unknown, record: SimplifiedRow) => (
          <InputNumber
            data-testid={`base-offset-${record.liftIndex}`}
            size="small"
            min={0}
            max={65535}
            value={simpleBaseOffsets[record.liftIndex] ?? 0}
            onChange={(val) => handleSimpleCellChange(record.liftIndex, val)}
            style={{ width: 120 }}
          />
        ),
      },
    ],
    [autoStride, excludedRows, simpleBaseOffsets, handleSimpleCellChange],
  );

  if (mappedTypes.length === 0) {
    return null;
  }

  function headerLabel(): string {
    if (isSimplified) return "Lift Register Offsets";
    if (byRegisterType) return "Register Columns";
    return "By LN2 Category";
  }

  function descriptionText(): string {
    const strideNote =
      autoStride && lifts.length >= 3 ? " Editing Car 1 or Car 2 auto-fills remaining rows." : "";
    if (isSimplified) {
      return `Each lift's base offset is added to the adapter's per-unit values for all message types. Final address = adapter base + lift base offset.${strideNote}`;
    }
    if (byRegisterType) {
      return `Each cell is the register offset for that lift and Modbus register type. All LN2 message categories sharing the same register type use this offset.${strideNote}`;
    }
    return `Each cell is the register offset added to the adapter base address for that lift and message type. Final address = base + offset.${strideNote} Ranges shown as [start, end) where [ is inclusive and ) is exclusive.`;
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          marginBottom: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: 500 }}>{headerLabel()}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {adapterDefaults !== undefined && mappedTypes.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#888" }}>Simplified</span>
              <Switch
                size="small"
                checked={isSimplified}
                onChange={handleEnterSimplified}
                data-testid="simplified-mode-toggle"
              />
            </div>
          )}
          {!isSimplified && registerTypeMappedTypes && registerTypeMappedTypes.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#888" }}>By Register Type</span>
              <Switch
                size="small"
                checked={byRegisterType}
                onChange={handleToggleRegisterType}
                data-testid="register-type-toggle"
              />
            </div>
          )}
          {((!isSimplified && lifts.length >= 2) || (isSimplified && lifts.length >= 3)) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#888" }}>Auto Stride</span>
              <Switch size="small" checked={autoStride} onChange={setAutoStride} />
            </div>
          )}
          <Tooltip title="Re-check overlaps against current values">
            <Button size="small" onClick={handleRefresh} data-testid="offset-matrix-refresh">
              Refresh
            </Button>
          </Tooltip>
          <Popconfirm
            title={
              <div style={{ maxWidth: 320 }}>
                <div style={{ fontWeight: 500 }}>Reset bank overrides to adapter defaults?</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  {isSimplified
                    ? "Sets every lift's base offset to 0. Adapter defaults are preserved."
                    : "Clears every per-lift override so each cell falls back to the adapter's initial + car × delta value."}
                </div>
              </div>
            }
            okText="Reset"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={handleReset}
          >
            <Button size="small" danger data-testid="offset-matrix-reset">
              Reset
            </Button>
          </Popconfirm>
        </div>
      </div>
      <div style={{ marginBottom: 8, color: "#888", fontSize: 12 }}>{descriptionText()}</div>
      {!isSimplified && hasOverlaps && (
        <Alert
          type="error"
          showIcon
          message="Register overlap detected"
          description="Some register ranges overlap — highlighted cells show conflicts. Adjust offsets to avoid collisions."
          style={{ marginBottom: 8 }}
        />
      )}
      {!isSimplified && autoStride && hasDeviations && (
        <Alert
          type="warning"
          showIcon
          message={`${strideDeviations.size} cell${
            strideDeviations.size > 1 ? "s" : ""
          } break the auto-stride pattern`}
          description="Orange cells deviate from the stride computed from Car 1 and Car 2. Hover over them to see the expected value."
          style={{ marginBottom: 8 }}
        />
      )}
      {isSimplified ? (
        <Table
          columns={simplifiedColumns}
          dataSource={simplifiedDataSource}
          pagination={false}
          size="small"
          bordered
        />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          scroll={activeMappedTypes.length > 4 ? { x: "max-content" } : undefined}
          bordered
        />
      )}
    </div>
  );
}
